import { defineStore } from 'pinia'
import { ref } from 'vue'

import { tabsApi } from '@/api/client'
import type { GetRiderTabs200ResponseTabsInner } from '@/openapi'

import { useAuthStore } from './auth'

type RiderTab = GetRiderTabs200ResponseTabsInner

const REFETCH_DEBOUNCE_MS = 500

type SyncEvent = {
  name: string
  locationEventId?: number
  data?: any
}

class UnknownTabError extends Error {
  constructor(public readonly tabId: string) {
    super(`Unknown tab ${tabId}`)
  }
}

export const useTabsStore = defineStore('tabs', () => {
  const tabs = ref<RiderTab[]>([])
  const currentEventId = ref<number>(0)

  let refetchTimer: ReturnType<typeof setTimeout> | null = null

  async function refreshTabs() {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated || !authStore.selectedLocationId) return

    const response = await tabsApi.getRiderTabs()
    tabs.value = response.tabs
    currentEventId.value = response.currentEventId
  }

  function scheduleRefetch() {
    if (refetchTimer) return
    refetchTimer = setTimeout(() => {
      refetchTimer = null
      refreshTabs()
    }, REFETCH_DEBOUNCE_MS)
  }

  function findTab(tabId: string) {
    return tabs.value.find(t => t.tabId === tabId)
  }

  function requireTab(tabId: string): RiderTab {
    const tab = findTab(tabId)
    if (!tab) throw new UnknownTabError(tabId)
    return tab
  }

  function removeTab(tabId: string) {
    tabs.value = tabs.value.filter(t => t.tabId !== tabId)
  }

  function buildTabFromPayload(rawTab: any): RiderTab | null {
    if (!rawTab?.id || !rawTab?.deliveryOrder) return null
    const deliveryOrder = rawTab.deliveryOrder
    if (deliveryOrder.cancelTime) return null
    const statuses = deliveryOrder.deliveryOrderStatuses ?? []
    const latest = statuses.length
      ? statuses.reduce((acc: any, current: any) =>
          new Date(current.creationTime).getTime() > new Date(acc.creationTime).getTime()
            ? current
            : acc
        )
      : null

    return {
      tabId: rawTab.id,
      code: String(rawTab.code ?? ''),
      status: latest ? { name: latest.status } : undefined
    }
  }

  function upsertTabFromPayload(rawTab: any) {
    const built = buildTabFromPayload(rawTab)
    if (!built) return
    const existing = findTab(built.tabId)
    if (existing) {
      Object.assign(existing, built)
    } else {
      tabs.value = [...tabs.value, built]
    }
  }

  function handleEvent(event: SyncEvent) {
    if (!event?.name || !event?.data) return
    const tabId: string | undefined = event.data.tabId ?? event.data.id

    switch (event.name) {
      case 'tabOpened':
        upsertTabFromPayload(event.data)
        return

      case 'tabOpenedWithCustomer':
        upsertTabFromPayload(event.data.tab ?? event.data)
        return

      case 'tabReopened':
        scheduleRefetch()
        return

      case 'tabClosedWithInfo':
      case 'deliveryCancelled':
        if (tabId && findTab(tabId)) removeTab(tabId)
        return

      case 'deliveryOrderStatusUpdated': {
        if (!tabId) return
        const tab = requireTab(tabId)
        tab.status = { name: event.data.newStatus as string }
        return
      }

      case 'tabCodeGenerated': {
        if (!tabId) return
        const tab = requireTab(tabId)
        tab.code = String(event.data.code)
        return
      }

      default:
        return
    }
  }

  function applyEvent(event: SyncEvent) {
    if (typeof event?.locationEventId === 'number') {
      currentEventId.value = Math.max(currentEventId.value, event.locationEventId)
    }
    try {
      handleEvent(event)
    } catch (error) {
      if (error instanceof UnknownTabError) {
        scheduleRefetch()
      }
    }
  }

  return {
    tabs,
    currentEventId,

    refreshTabs,
    applyEvent
  }
})
